<template>
  <Teleport to="body">
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
  </Teleport>

  <div v-if="visible" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-header">
        <h2 class="modal-title">{{ template?.template_name || 'Care Template' }}</h2>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="closeIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div v-if="loading" class="loading-overlay">
        <LoadingIndicator text="Loading..." />
      </div>

      <div v-else-if="error" class="error-container">
        <p class="error-text">{{ error }}</p>
      </div>

      <div v-else class="modal-body">
        <div class="working-hours">Estimated working hours: {{ formatWorkingHours }}</div>

        <div class="tab-switcher">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'baseline' }"
            @click="activeTab = 'baseline'"
            type="button"
          >
            Baseline
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'routine' }"
            @click="activeTab = 'routine'"
            type="button"
          >
            Routine
          </button>
        </div>

        <div class="checklist-container">
          <div class="select-all-wrapper">
            <div class="checkbox-wrapper" @click="toggleAll">
              <div class="checkbox" :class="{ checked: isAllSelected }">
                <svg v-if="isAllSelected" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="checkbox-label">All</span>
            </div>
          </div>

          <div class="checklist-items">
            <div
              v-for="item in currentItems"
              :key="item.id"
              class="checkbox-wrapper"
              @click="toggleItem(item.id)"
            >
              <div class="checkbox" :class="{ checked: isSelected(item.id) }">
                <svg v-if="isSelected(item.id)" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span class="item-code">{{ item.item_id }}</span>
              <span class="item-name">{{ item.item_name }}</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="handleClose" type="button">Cancel</button>
          <button class="btn-submit" type="button" :disabled="submitting" @click="handleSubmit">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { updateTemplateItems, type CareTemplate, type CareTemplateItem } from '@/api/EditPremium'
import { getCareTemplateDetail, type CareTemplateDetailResponse } from '@/api/caredetail'
import { getChecklistList, type ChecklistItem } from '@/api/checklist'
import ToastComponent from '@/components/ToastComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import closeIcon from '@/assets/Fill 1@2x.png'

interface Props {
  visible: boolean
  templateId: number | null
  planKey: 'essential_care' | 'enhanced_care'
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const loading = ref(false)
const error = ref('')
const submitting = ref(false)
const activeTab = ref<'routine' | 'baseline'>('baseline')

const template = ref<CareTemplate | null>(null)
const selectedItems = ref<Set<number>>(new Set())
const allChecklistItems = ref<ChecklistItem[]>([])

const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

const formatWorkingHours = computed(() => {
  if (!template.value) return '0 hrs'
  if (template.value.estimated_hours > 0) {
    return `${template.value.estimated_hours} hrs`
  }
  if (template.value.estimated_minutes > 0) {
    return `${template.value.estimated_minutes / 60} hrs`
  }
  return '0 hrs'
})

const currentItems = computed<CareTemplateItem[]>(() => {
  if (!template.value) return []

  return activeTab.value === 'routine'
    ? template.value.routine_items || []
    : template.value.baseline_items || []
})

const isAllSelected = computed(() => {
  if (currentItems.value.length === 0) return false
  return currentItems.value.every((item) => selectedItems.value.has(item.id))
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = type
  toastMessage.value = message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
  }, 3500)
}

function isSelected(itemId: number): boolean {
  return selectedItems.value.has(itemId)
}

function toggleItem(itemId: number): void {
  if (selectedItems.value.has(itemId)) {
    selectedItems.value.delete(itemId)
  } else {
    selectedItems.value.add(itemId)
  }
}

function toggleAll(): void {
  if (isAllSelected.value) {
    selectedItems.value.clear()
  } else {
    currentItems.value.forEach((item) => {
      selectedItems.value.add(item.id)
    })
  }
}

async function fetchTemplate(): Promise<void> {
  if (!props.templateId) return

  loading.value = true
  error.value = ''

  try {
    await loadChecklistItemsIfNeeded()

    const baselineItems = allChecklistItems.value
      .filter((item) => item.type === 'baseline')
      .map((item) => ({
        id: item.id,
        item_id: item.item_id,
        item_name: item.item_name,
        item_name_zh: item.item_name_zh,
        type: item.type,
        type_text: item.type_text,
        section: item.section,
        duration_minutes: item.duration_minutes,
        duration_text: item.duration_text,
        status: item.status,
      }))

    const routineItems = allChecklistItems.value
      .filter((item) => item.type === 'routine')
      .map((item) => ({
        id: item.id,
        item_id: item.item_id,
        item_name: item.item_name,
        item_name_zh: item.item_name_zh,
        type: item.type,
        type_text: item.type_text,
        section: item.section,
        duration_minutes: item.duration_minutes,
        duration_text: item.duration_text,
        status: item.status,
      }))

    const response: CareTemplateDetailResponse = await getCareTemplateDetail({
      id: props.templateId,
    })

    if (response.code === 200 && response.data) {
      const detailData = response.data

      template.value = {
        id: detailData.id,
        plan_key: detailData.plan_key,
        plan_text: '',
        template_key: '',
        template_name: detailData.template_name,
        period_type: '',
        month: detailData.month || 0,
        start_month: detailData.start_month,
        end_month: detailData.end_month,
        estimated_minutes: detailData.estimated_minutes,
        estimated_hours: detailData.estimated_hours,
        items: [],
        baseline_items: baselineItems,
        routine_items: routineItems,
        item_count: 0,
        status: 0,
        status_text: '',
        sort: 0,
        create_time: '',
        update_time: '',
      }

      selectedItems.value = new Set([
        ...detailData.baseline_checklist_ids,
        ...detailData.routine_checklist_ids,
      ])

      console.log('Template loaded successfully')
      console.log('Baseline items count:', baselineItems.length)
      console.log('Routine items count:', routineItems.length)
      console.log('Selected items:', selectedItems.value)
    } else {
      error.value = response.msg || 'Failed to load template'
    }
  } catch (err) {
    console.error('Fetch template error:', err)
    error.value = 'Network error, please try again later'
  } finally {
    loading.value = false
  }
}
async function loadChecklistItemsIfNeeded(): Promise<void> {
  if (allChecklistItems.value.length > 0) return

  try {
    const response = await getChecklistList({
      page: 1,
      page_size: 1000,
    })

    if (response.code === 200 && response.data) {
      allChecklistItems.value = response.data.list
      console.log('Loaded checklist items:', allChecklistItems.value.length)
    }
  } catch {
    console.error('Failed to load checklist items')
  }
}

async function handleSubmit(): Promise<void> {

  submitting.value = true

  try {
    const routineIds = (template.value?.routine_items || [])
      .filter((item) => selectedItems.value.has(item.id))
      .map((item) => item.id)

    const baselineIds = (template.value?.baseline_items || [])
      .filter((item) => selectedItems.value.has(item.id))
      .map((item) => item.id)

    const response = await updateTemplateItems({
      template_id: props.templateId!,
      baseline_checklist_ids: baselineIds,
      routine_checklist_ids: routineIds,
    })

    if (response.code === 200) {
      emit('saved')
      await loadChecklistItemsIfNeeded() // 等待数据加载完成
      emit('close') // 数据加载完成后关闭弹窗
      showToast('success', 'Successfully saved') // 显示toast
    } else {
      showToast('error', response.msg || 'Failed to save')
    }
  } catch {
    showToast('error', 'Failed to save')
  } finally {
    submitting.value = false
  }
}

function handleClose(): void {
  emit('close')
}


watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.templateId) {
      loading.value = true
      error.value = ''
      activeTab.value = 'baseline'
      void fetchTemplate()
    }
  },
)

watch(
  () => props.templateId,
  (newId) => {
    if (props.visible && newId) {
      loading.value = true
      error.value = ''
      activeTab.value = 'baseline'
      void fetchTemplate()
    }
  },
)
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 16px;
  width: 530px;
  height: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  position: relative;
  z-index: 2;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

:deep(.hm-loading) {
  padding: 0;
}

.modal-title {
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 15px;
  height: 15px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 80px;
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.error-text {
  color: #ff4d4f;
  font-size: 14px;
}

.working-hours {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  margin-bottom: 16px;
}

.tab-switcher {
  display: flex;
  background-color: #f5f6fb;
  border-radius: 16px;
  padding: 0;
  margin-bottom: 16px;
  height: 32px;
}

.tab-btn {
  flex: 1;
  height: 32px;
  padding: 0 20px;
  border: none;
  border-radius: 16px;
  background-color: transparent;
  color: #999999;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background-color: #000000;
  color: #ffffff;
}

.tab-btn:hover:not(.active) {
  color: #999999;
}

.checklist-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.select-all-wrapper {
  padding-bottom: 12px;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.checkbox svg {
  width: 12px;
  height: 12px;
}

.checkbox-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.item-code {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  color: #000000;
  font-weight: 400;
  flex-shrink: 0;
  padding-right: 10px;
}

.item-name {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  flex: 1;
  line-height: 1.4;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 16px 24px 24px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border-radius: 0 0 16px 16px;
}

.btn-cancel {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 24px;
  background-color: #ffffff;
  color: #000000;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-submit {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 24px;
  background-color: #000000;
  color: #ffffff;
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-submit:hover:not(:disabled) {
  background-color: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
